/**
 * useUnsavedChanges Hook
 *
 * @version 1.0
 * @updated 2026-02-16
 * @module core/layouts/panels
 *
 * @description
 * Hook para detectar y gestionar cambios sin guardar en formularios.
 * Compara el estado actual con el estado original y determina si hay cambios.
 *
 * @remarks
 * **Características**:
 * - Detecta cambios automáticamente comparando objetos
 * - Evita preguntas innecesarias si no hay cambios
 * - Proporciona función para confirmar descarte de cambios
 * - Resetea el estado al guardar
 *
 * @example
 * ```tsx
 * const { hasUnsavedChanges, confirmDiscard, markAsSaved } = useUnsavedChanges(
 *   originalData,
 *   currentData
 * );
 *
 * const handleClose = async () => {
 *   if (hasUnsavedChanges && !await confirmDiscard()) {
 *     return; // Usuario canceló
 *   }
 *   onClose();
 * };
 * ```
 */

import { useMemo, useCallback } from 'react';
import { ConfirmDialog } from '../../../components';

/**
 * Compara dos objetos profundamente para detectar cambios
 */
function deepEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true;

  if (
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object' ||
    obj1 === null ||
    obj2 === null
  ) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }

  return true;
}

/**
 * Opciones para personalizar el mensaje de confirmación
 */
export interface UnsavedChangesOptions {
  /**
   * Mensaje personalizado para el diálogo de confirmación
   */
  confirmMessage?: string;
}

/**
 * Resultado del hook useUnsavedChanges
 */
export interface UnsavedChangesResult {
  /**
   * Indica si hay cambios sin guardar
   */
  hasUnsavedChanges: boolean;

  /**
   * Muestra diálogo de confirmación si hay cambios sin guardar
   * @returns true si el usuario confirma descartar, false si cancela
   */
  confirmDiscard: () => Promise<boolean>;

  /**
   * Marca los cambios como guardados (actualiza el snapshot)
   */
  markAsSaved: (newData: any) => void;
}

/**
 * Hook para gestionar cambios sin guardar
 *
 * @param originalData - Datos originales (estado inicial)
 * @param currentData - Datos actuales (estado modificado)
 * @param options - Opciones de configuración
 * @returns Objeto con hasUnsavedChanges, confirmDiscard y markAsSaved
 *
 * @example
 * ```tsx
 * const [formData, setFormData] = useState(initialData);
 * const { hasUnsavedChanges, confirmDiscard, markAsSaved } = useUnsavedChanges(
 *   initialData,
 *   formData
 * );
 *
 * const handleSave = async () => {
 *   await api.save(formData);
 *   markAsSaved(formData); // Actualiza el snapshot
 * };
 * ```
 */
export function useUnsavedChanges(
  originalData: any,
  currentData: any,
  options: UnsavedChangesOptions = {}
): UnsavedChangesResult {
  const {
    confirmMessage = '¿Descartar cambios sin guardar?\n\nLos cambios realizados se perderán.',
  } = options;

  /**
   * Detecta si hay cambios comparando datos originales con actuales
   */
  const hasUnsavedChanges = useMemo(() => {
    if (!originalData || !currentData) {
      return false;
    }
    const equal = deepEqual(originalData, currentData);
    const hasChanges = !equal;
    return hasChanges;
  }, [originalData, currentData]);

  /**
   * Confirma descarte de cambios
   * Memoizado con useCallback para evitar recreación innecesaria
   */
  const confirmDiscard = useCallback(async (): Promise<boolean> => {
    if (!hasUnsavedChanges) {
      console.log('[useUnsavedChanges] ✓ No hay cambios, permitiendo cierre');
      return true; // No hay cambios, no preguntar
    }

    console.warn('[useUnsavedChanges] ⚠ Cambios detectados:');
    console.log('Original:', originalData);
    console.log('Current:', currentData);

    // Parsear mensaje: primera línea es el título, resto es el mensaje
    const lines = confirmMessage.split('\n');
    const title = lines[0].trim();
    const message = lines.slice(1).join('\n').trim();

    const result = await ConfirmDialog.show({
      title,
      message,
      confirmText: 'Aceptar',
      cancelText: 'Cancelar',
      type: 'warning',
    });

    console.log('[useUnsavedChanges] Usuario respondió:', result ? 'Aceptar (descartar)' : 'Cancelar');
    return result;
  }, [hasUnsavedChanges, confirmMessage, originalData, currentData]);

  /**
   * Marca como guardado actualizando el snapshot original
   */
  const markAsSaved = (_newData: any): void => {
    // Esta función es principalmente informativa
    // El snapshot se actualiza cuando el componente padre actualiza originalData
    console.log('[useUnsavedChanges] Cambios guardados');
  };

  return {
    hasUnsavedChanges,
    confirmDiscard,
    markAsSaved,
  };
}
